import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    User,
    CalendarDays,
    ArrowRight,
    Newspaper,
    AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BASE_URL, blogsApi } from "../../../../api/api";
import { token } from "../../../../api/auth";

const BlogCard = () => {
    const navigate = useNavigate();
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogs = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${BASE_URL}${blogsApi}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const data = await response.json();

                // Ensure data is an array
                if (Array.isArray(data)) {
                    setBlogPosts(data);
                } else if (data?.data && Array.isArray(data.data)) {
                    setBlogPosts(data.data);
                } else {
                    setBlogPosts([]);
                }
            } catch (error) {
                console.error("Error fetching blogs:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, []);

    const handleRedirect = (post) => {
        navigate("/blog-details", { state: post });
    };

    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.style.objectFit = "contain";
        e.target.style.backgroundColor = "#f0f0f0";
        e.target.style.filter = "grayscale(100%)";
        e.target.style.opacity = "0.5";
        e.target.src = "/assets/logo.png";
    };

    // Active posts filter (optional, based on standard project conventions)
    const activePosts = blogPosts.filter(
        (post) =>
            post.status === undefined ||
            post.status === 1 ||
            post.status === true,
    );

    // Animation variants matching the established theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full border-t border-gray-200 min-h-screen flex flex-col">
            <div className="max-w-7xl mx-auto w-full">
                {/* Standardized Editorial Header */}
                <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-gray-200 pb-6">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="max-w-2xl"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                            Explore Our{" "}
                            <span className="text-yellow-500">Blogs.</span>
                        </h2>
                        <p className="text-gray-500 text-lg font-medium leading-relaxed">
                            In-depth analysis, preparation strategies, and
                            continuous inspiration curated by UPSC experts.
                        </p>
                    </motion.div>
                </div>

                {/* Content Grid */}
                {loading ? (
                    /* Skeleton Loader */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm animate-pulse flex flex-col overflow-hidden"
                            >
                                <div className="w-full aspect-video bg-gray-200"></div>
                                <div className="p-6 flex flex-col flex-grow">
                                    <div className="h-4 bg-gray-100 rounded w-1/3 mb-4"></div>
                                    <div className="h-6 bg-gray-200 rounded w-full mb-3"></div>
                                    <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
                                    <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
                                    <div className="h-4 bg-gray-100 rounded w-4/5 mb-6"></div>
                                    <div className="mt-auto pt-4 border-t border-gray-100">
                                        <div className="h-4 bg-gray-100 rounded w-1/4"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : activePosts.length === 0 ? (
                    /* Empty State */
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center max-w-2xl mx-auto mt-8"
                    >
                        <div className="w-16 h-16 bg-gray-50 border border-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Newspaper size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-neutral-900 mb-2">
                            No Articles Found
                        </h3>
                        <p className="text-gray-500 font-medium text-sm">
                            We are currently preparing new insights and
                            strategies. Check back soon for the latest updates.
                        </p>
                    </motion.div>
                ) : (
                    /* Article Grid */
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <AnimatePresence>
                            {activePosts.map((post, index) => (
                                <motion.div
                                    variants={itemVariants}
                                    key={post.id || index}
                                    layout
                                    onClick={() => handleRedirect(post)}
                                    className="group relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 flex flex-col overflow-hidden cursor-pointer"
                                >
                                    {/* Signature Hover Accent Line */}
                                    <div className="absolute top-0 left-0 w-full h-[3px] bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500 ease-out z-20"></div>

                                    {/* Image Header */}
                                    <div className="w-full aspect-video relative overflow-hidden bg-gray-100 border-b border-gray-100 z-10">
                                        <img
                                                                                         src={Array.isArray(post.image) ? post.image[0] : (post.image || "/assets/placeholder-blog.jpg")}
                                            alt={post.title}
                                            onError={handleImageError}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out"
                                        />
                                        <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/10 transition-colors duration-300"></div>
                                    </div>

                                    {/* Card Content */}
                                    <div className="p-6 flex flex-col flex-grow">
                                        {/* Meta Info */}
                                        <div className="flex items-center gap-4 text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-4">
                                            {post.author && (
                                                <span className="flex items-center text-red-700">
                                                    <User
                                                        size={12}
                                                        className="mr-1.5"
                                                    />
                                                    {post.author}
                                                </span>
                                            )}
                                            <span className="flex items-center">
                                                <CalendarDays
                                                    size={12}
                                                    className="mr-1.5 text-yellow-500"
                                                />
                                                {post.created_at
                                                    ? new Intl.DateTimeFormat(
                                                          "en-GB",
                                                          {
                                                              day: "2-digit",
                                                              month: "short",
                                                              year: "numeric",
                                                          },
                                                      ).format(
                                                          new Date(
                                                              post.created_at,
                                                          ),
                                                      )
                                                    : "N/A"}
                                            </span>
                                        </div>

                                        {/* Title */}
                                        <h3 className="text-xl font-black text-neutral-900 mb-3 line-clamp-2 group-hover:text-red-700 transition-colors leading-snug">
                                            {post.title}
                                        </h3>

                                        {/* Snippet */}
                                        <p className="text-sm text-gray-500 font-medium leading-relaxed line-clamp-3 mb-6 flex-grow">
                                            {post.short_description ||
                                                "Read more to discover comprehensive analysis and expert insights on this topic."}
                                        </p>

                                        {/* Footer Action */}
                                        <div className="mt-auto pt-4 border-t border-gray-100 flex items-center text-sm font-bold text-neutral-900 group-hover:text-yellow-600 transition-colors">
                                            Read Article
                                            <ArrowRight
                                                size={16}
                                                className="ml-2 transform group-hover:translate-x-1 transition-transform"
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default BlogCard;
